/*
 * The fleet-location screens' styles, in the shape the rest of the product uses:
 * a `styles.default` / `styles.medium` / `styles.small` object read through `getStyle`.
 *
 * SEPARATE FROM THE COMPONENTS so the contract can be tested. These screens shipped with
 * browser-default black text on the fleet portal's dark photographic background -- headings
 * and half the vehicle table were barely readable -- and every automated check called it a
 * pass, because nothing looks at colour. A style object can be asserted against; a colour
 * buried in JSX cannot.
 *
 * The values are the portal's own, read from the screens beside these:
 * VehicleListView (table headers: TitilliumWeb-Bold, #FFFFFF, 8px padding) and
 * ChainPartsUsageView (secondary text: rgba(255,255,255,0.7); values: #FFFFFF).
 */

// The portal's palette, named rather than repeated. Anything that renders on the dark
// background must come from here.
const TEXT = '#FFFFFF';
const TEXT_SECONDARY = 'rgba(255, 255, 255, 0.7)';
const RULE = 'rgba(255, 255, 255, 0.3)';
const BOLD = 'TitilliumWeb-Bold';

// Status colours keep their meaning against the dark background: the fresh green the
// screens already used is unreadable on it, so both are lifted.
const OK = '#7ED321';
const WARN = '#FFC46B';
const DANGER = '#FF8A80';

// The product's action blue is #0095da, chosen for a light background. On this dark one it
// reads at about 3:1 -- fine for a large heading, short of AA for a 14px button label -- so
// this is the same hue lifted until it passes. The contrast test enforces that; do not
// restore the darker value here without moving the button to a filled treatment.
const ACCENT = '#4FC3F7';

const fleetLocationStyles = {
  default: {
    container: {
      width: '100%',
      padding: '24px 16px 32px 16px',
    },
    heading: {
      fontFamily: BOLD,
      color: TEXT,
      fontSize: 24,
      margin: '0 0 4px 0',
    },
    subheading: {
      fontFamily: BOLD,
      color: TEXT,
      fontSize: 18,
      margin: '32px 0 8px 0',
    },
    summary: {
      color: TEXT_SECONDARY,
      fontSize: 15,
      margin: '0 0 16px 0',
    },
    map: {
      width: '100%',
      height: 480,
      marginBottom: 24,
      borderRadius: 4,
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    tableHeader: {
      fontFamily: BOLD,
      color: TEXT,
      textAlign: 'left',
      padding: '8px 8px 8px 8px',
      borderBottom: `1px solid ${RULE}`,
    },
    tableCell: {
      color: TEXT,
      fontSize: 15,
      padding: '8px 8px 8px 8px',
      borderBottom: `1px solid ${RULE}`,
    },
    tableCellMuted: {
      color: TEXT_SECONDARY,
      fontSize: 15,
      padding: '8px 8px 8px 8px',
      borderBottom: `1px solid ${RULE}`,
    },
    statusCurrent: {
      color: OK,
      fontSize: 15,
      padding: '8px 8px 8px 8px',
      borderBottom: `1px solid ${RULE}`,
    },
    statusStale: {
      color: TEXT_SECONDARY,
      fontSize: 15,
      padding: '8px 8px 8px 8px',
      borderBottom: `1px solid ${RULE}`,
    },
    notice: {
      color: TEXT,
      fontSize: 15,
      background: 'rgba(255, 255, 255, 0.08)',
      border: `1px solid ${RULE}`,
      borderRadius: 4,
      padding: '12px 16px',
      marginBottom: 16,
    },
    staleBanner: {
      color: WARN,
      fontSize: 15,
      background: 'rgba(255, 196, 107, 0.12)',
      border: '1px solid rgba(255, 196, 107, 0.5)',
      borderRadius: 4,
      padding: '8px 12px',
      margin: '0 0 12px 0',
    },
    error: {
      color: DANGER,
      fontSize: 15,
      margin: '0 0 12px 0',
    },
    label: {
      display: 'block',
      color: TEXT_SECONDARY,
      fontSize: 13,
      marginBottom: 4,
    },
    input: {
      width: '100%',
      padding: '8px 10px',
      fontSize: 15,
      color: TEXT,
      background: 'rgba(0, 0, 0, 0.25)',
      border: `1px solid ${RULE}`,
      borderRadius: 4,
    },
    form: {
      marginTop: 8,
      maxWidth: 460,
    },
    // MATCHES CBButtonSubmit's look -- rounded, blue outline, blue label -- but stays a real
    // <button>. That component is a <div> with an onClick, which inside a <form> submits
    // nothing and is unreachable by keyboard. Borrow the appearance, keep the semantics.
    button: {
      marginTop: 12,
      padding: '10px 24px',
      fontSize: 14,
      fontWeight: 700,
      fontFamily: BOLD,
      color: ACCENT,
      background: 'transparent',
      border: `2px solid ${ACCENT}`,
      borderRadius: 50,
      cursor: 'pointer',
    },
    buttonDisabled: {
      marginTop: 12,
      padding: '10px 24px',
      fontSize: 14,
      fontWeight: 700,
      fontFamily: BOLD,
      color: ACCENT,
      background: 'transparent',
      border: `2px solid ${ACCENT}`,
      borderRadius: 50,
      cursor: 'default',
      opacity: 0.5,
    },
    buttonSmall: {
      padding: '4px 14px',
      fontSize: 13,
      fontWeight: 700,
      color: ACCENT,
      background: 'transparent',
      border: `1px solid ${ACCENT}`,
      borderRadius: 50,
      cursor: 'pointer',
    },
    // A client id is an 86-character token. Left to itself it forces the table wider than
    // the screen or wraps mid-word into a ragged block; this keeps it inside its column and
    // breaks it at a sensible width.
    tableCellToken: {
      color: TEXT_SECONDARY,
      fontSize: 13,
      padding: '8px 8px 8px 8px',
      borderBottom: `1px solid ${RULE}`,
      maxWidth: 320,
      wordBreak: 'break-all',
    },
    field: {
      display: 'block',
      marginBottom: 12,
    },
    vehicleName: {
      fontFamily: BOLD,
      color: TEXT,
      fontSize: 18,
      margin: '0 0 4px 0',
    },
    vehicleModel: {
      color: TEXT_SECONDARY,
      fontSize: 18,
    },
    vin: {
      color: TEXT_SECONDARY,
      fontSize: 13,
      margin: '0 0 12px 0',
    },
  },
  medium: {
    container: {
      padding: '16px 16px 24px 16px',
    },
    map: {
      height: 380,
    },
  },
  small: {
    container: {
      padding: '12px 12px 24px 12px',
    },
    heading: {
      fontSize: 20,
    },
    map: {
      height: 300,
    },
  },
};

export { fleetLocationStyles, TEXT, TEXT_SECONDARY, RULE, OK, WARN, DANGER, ACCENT };
